import { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ChartDataset,
  ChartOptions,
  ChartType,
  Interaction,
  InteractionItem,
  InteractionModeFunction,
  Plugin,
  Point,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import { Currency } from "../../i18n/Currency";
import { percentageNoDecimals } from "../../shared/percentage";
import { getLegendLabels, getTicks } from "../../utils/chartjsTheming";

/** The height of a chart of risk, in px. */
export const RISK_CHART_HEIGHT = 320;

/** A line of a chart of risk shows no points until the pointer is over one. */
export const RISK_LINE_POINTS = {
  pointRadius: 0,
  pointHoverRadius: 4,
  pointHitRadius: 20,
} as const;

/**
 * The scales the charts of risk share: the cumulative probability of the
 * outcomes across, from none to all of it, and money up.
 */
export function riskChartScales({
  theme,
  currency,
  axisTitle,
  y,
}: {
  theme: Theme;
  currency: Currency;
  // says in what order the outcomes follow one another
  axisTitle: string;
  // how far the scale of the money reaches
  y: { min: number; max: number } | { beginAtZero: true };
}): NonNullable<ChartOptions<"line">["scales"]> {
  return {
    x: {
      type: "linear",
      min: 0,
      max: 1,
      title: {
        display: true,
        text: axisTitle,
        color: theme.palette.text.secondary,
        font: getTicks(theme).font,
      },
      grid: { display: false },
      ticks: {
        ...getTicks(theme),
        stepSize: 0.25,
        callback: (value) =>
          typeof value === "number"
            ? percentageNoDecimals.format(value)
            : value,
      },
    },
    y: {
      ...y,
      grid: {
        // the line of zero stands out from the lines of the other amounts
        color: (context) =>
          context.tick.value === 0
            ? theme.palette.text.disabled
            : theme.palette.divider,
      },
      ticks: {
        ...getTicks(theme),
        // the ends of the scale are no round amounts: named, they would
        // stand right beside the round ones next to them
        includeBounds: false,
        callback: (value) =>
          typeof value === "number" ? currency.formatCompact(value) : value,
      },
    },
  };
}

/** The legend of a chart of risk, at its upper right. */
export function riskChartLegend(theme: Theme) {
  return {
    position: "top",
    align: "end",
    labels: { ...getLegendLabels(theme), boxWidth: 18, boxHeight: 10 },
  } as const;
}

/** How strongly the band behind the steps of settlements is shaded. */
const SETTLEMENT_SHADE_ALPHA = 0.07;

/** The colour of the band behind the steps of settlements. */
export const settlementShade = (theme: Theme) =>
  alpha(theme.palette.text.primary, SETTLEMENT_SHADE_ALPHA);

/** The stretches of the scale of probability that settlements take. */
export type SettlementShadingOptions = {
  bands: { from: number; to: number }[];
  color: string;
};

declare module "chart.js" {
  interface PluginOptionsByType<TType extends ChartType> {
    // the bands of a chart of risk, read on every draw (see SETTLEMENT_SHADING)
    settlementShading?: SettlementShadingOptions & { _type?: TType };
  }
}

/**
 * Shades the stretches of the scale of probability that settlements take, as
 * bands behind the lines, the full height of the chart. The bands come with
 * the options, not with the plugin: a chart keeps its plugins from when it is
 * made, and takes its options anew whenever they change.
 */
export const SETTLEMENT_SHADING: Plugin<"line", SettlementShadingOptions> = {
  id: "settlementShading",
  beforeDatasetsDraw: ({ ctx, chartArea, scales: { x } }, _args, options) => {
    if (!options?.bands?.length) return;
    ctx.save();
    ctx.fillStyle = options.color;
    for (const { from, to } of options.bands) {
      const left = x.getPixelForValue(from);
      ctx.fillRect(
        left,
        chartArea.top,
        x.getPixelForValue(to) - left,
        chartArea.bottom - chartArea.top,
      );
    }
    ctx.restore();
  },
};

/** The entry of the legend that names the shaded bands: a series with no line. */
export const settlementLegendEntry = (
  label: string,
  color: string,
): ChartDataset<"line", Point[]> => ({
  label,
  data: [],
  backgroundColor: color,
  borderColor: "transparent",
  borderWidth: 0,
});

declare module "chart.js" {
  interface InteractionModeMap {
    step: InteractionModeFunction;
  }
}

/**
 * Where the pointer is over a stepped line, the step it is over: of each
 * series, the last point at or before it across. A step runs from its point
 * to the next, so anywhere along it is its outcome, not only at its start.
 */
Interaction.modes.step = (chart, event) => {
  const { x } = getRelativePosition(event, chart);
  const at = chart.scales.x.getValueForPixel(x);
  if (at === undefined) return [];
  return chart.data.datasets.flatMap(
    (dataset, datasetIndex): InteractionItem[] => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!chart.isDatasetVisible(datasetIndex)) return [];
      const points = dataset.data as Point[];
      let index = -1;
      points.forEach((point, i) => {
        if (point.x !== null && point.x <= at) index = i;
      });
      return index < 0 || !meta.data[index]
        ? []
        : [{ element: meta.data[index], datasetIndex, index }];
    },
  );
};
